import { useState, useRef, useCallback } from 'react';

/**
 * Handles page-level drag-and-drop for the external AI file import flow.
 * Extracted from useWorkflowBuilderState to keep that hook under the 500-line cap.
 */
export function useWorkflowFileDrop(onImport: (content: string, fileName: string) => void) {
  const [dragOver, setDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.types.includes('Files')) setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) setDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOver(false);
      dragCounterRef.current = 0;
      const file = e.dataTransfer.files?.[0];
      if (!file) return;
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (!['txt', 'md', 'json', 'yaml', 'yml'].includes(ext ?? '')) return;
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') onImport(reader.result, file.name);
      };
      reader.readAsText(file);
    },
    [onImport],
  );

  return { dragOver, handleDragOver, handleDragEnter, handleDragLeave, handleDrop };
}
